import { Box, Typography, IconButton, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { createEditor, Editor, Element as SlateElement, Transforms, Node } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { useMemo, useCallback } from 'react';
import { FormatBold, FormatItalic, FormatUnderlined, Code, FormatListBulleted, FormatListNumbered } from '@mui/icons-material';
import ErrorComponent from './error-component';

// Update custom types to include lists
type CustomElement = {
  type: 'paragraph' | 'bulleted-list' | 'numbered-list' | 'list-item';
  children: { text: string }[];
};
type CustomText = { text: string; bold?: boolean; italic?: boolean; underline?: boolean; code?: boolean };

// Define the props interface
interface RHFRichTextEditorProps {
  name: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  initialValue?: CustomElement[];
}

interface MarkButtonProps {
  format: keyof Omit<CustomText, 'text'>;
  icon: React.ComponentType;
  editor: ReactEditor;
}

interface BlockButtonProps {
  format: Extract<CustomElement['type'], 'bulleted-list' | 'numbered-list'>;
  icon: React.ComponentType;
  editor: ReactEditor;
}

const LIST_TYPES = ['bulleted-list', 'numbered-list'];

const MarkButton = ({ format, icon: Icon, editor }: MarkButtonProps) => {
  const isActive = isMarkActive(editor, format);

  return (
    <IconButton
      size="small"
      onClick={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      sx={{
        color: isActive ? 'primary.main' : 'text.secondary',
        '&:hover': { backgroundColor: 'action.hover' },
        '&:focus': {
          outline: 'none',
        },
      }}
    >
      <Icon />
    </IconButton>
  );
};

type MarkFormat = keyof Omit<CustomText, 'text'>;

const isMarkActive = (editor: ReactEditor, format: MarkFormat) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const toggleMark = (editor: ReactEditor, format: MarkFormat) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const BlockButton = ({ format, icon: Icon, editor }: BlockButtonProps) => {
  const isActive = isBlockActive(editor, format);

  return (
    <IconButton
      size="small"
      onClick={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      sx={{
        color: isActive ? 'primary.main' : 'text.secondary',
        '&:hover': { backgroundColor: 'action.hover' },
        '&:focus': { outline: 'none' },
      }}
    >
      <Icon />
    </IconButton>
  );
};

const isBlockActive = (editor: ReactEditor, format: string) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    })
  );

  return !!match;
};

const toggleBlock = (editor: ReactEditor, format: string) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type as string),
    split: true,
  });

  const newProperties: Partial<CustomElement> = {
    type: isActive ? 'paragraph' : isList ? 'list-item' : (format as CustomElement['type']),
  };

  Transforms.setNodes<CustomElement>(editor, newProperties);

  if (!isActive && isList) {
    const block: CustomElement = { type: format as CustomElement['type'], children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const INITIAL_VALUE: CustomElement[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export default function RHFRichTextEditor({ name, label, helperText, placeholder, disabled = false, initialValue }: RHFRichTextEditorProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const editor = useMemo(() => {
    const slateEditor = withHistory(withReact(createEditor()));

    // Normalize the editor to ensure proper list handling
    const { normalizeNode } = slateEditor;
    slateEditor.normalizeNode = ([node, path]) => {
      if (SlateElement.isElement(node) && LIST_TYPES.includes(node.type)) {
        for (const [child, childPath] of Node.children(slateEditor, path)) {
          if (SlateElement.isElement(child) && child.type !== 'list-item') {
            Transforms.setNodes(slateEditor, { type: 'list-item' }, { at: childPath });
          }
        }
      }
      normalizeNode([node, path]);
    };

    return slateEditor;
  }, []);

  const handleChange = useCallback((value: CustomElement[]) => {
    // Ensure the value always has at least one node with proper structure
    const isValueEmpty = value.length === 0 || (value.length === 1 && value[0].children.length === 0);

    if (isValueEmpty) {
      return INITIAL_VALUE;
    }

    // Ensure each node has the correct structure
    return value.map((node) => {
      if (!node.children) {
        return { ...node, children: [{ text: '' }] };
      }
      return node;
    });
  }, []);

  const renderLeaf = useCallback((props: { attributes: React.HTMLAttributes<HTMLSpanElement>; children: React.ReactNode; leaf: CustomText }) => {
    const { attributes, leaf } = props;
    let { children } = props;

    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
    }

    if (leaf.underline) {
      children = <u>{children}</u>;
    }

    if (leaf.code) {
      children = <code>{children}</code>;
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  const renderElement = useCallback((props: { attributes: React.HTMLAttributes<HTMLElement>; children: React.ReactNode; element: CustomElement }) => {
    switch (props.element.type) {
      case 'bulleted-list':
        return <ul {...props.attributes}>{props.children}</ul>;
      case 'numbered-list':
        return <ol {...props.attributes}>{props.children}</ol>;
      case 'list-item':
        return <li {...props.attributes}>{props.children}</li>;
      default:
        return <p {...props.attributes}>{props.children}</p>;
    }
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!event.ctrlKey) return;

      switch (event.key) {
        case 'b': {
          event.preventDefault();
          toggleMark(editor, 'bold');
          break;
        }
        case 'i': {
          event.preventDefault();
          toggleMark(editor, 'italic');
          break;
        }
        case 'u': {
          event.preventDefault();
          toggleMark(editor, 'underline');
          break;
        }
      }
    },
    [editor]
  );

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue={initialValue || INITIAL_VALUE}
        render={({ field }) => (
          <>
            <Box
              sx={{
                border: (theme) => `1px solid ${errors[name] ? theme.palette.error.main : theme.palette.grey[300]}`,
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Slate
                editor={editor}
                initialValue={field.value || initialValue || INITIAL_VALUE}
                onChange={(value) => field.onChange(handleChange(value as CustomElement[]))}
              >
                {!disabled && (
                  <Box
                    sx={{
                      p: 1,
                      borderBottom: '1px solid',
                      borderColor: 'grey.200',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MarkButton format="bold" icon={FormatBold} editor={editor} />
                      <MarkButton format="italic" icon={FormatItalic} editor={editor} />
                      <MarkButton format="underline" icon={FormatUnderlined} editor={editor} />
                      <MarkButton format="code" icon={Code} editor={editor} />
                      <Box sx={{ borderLeft: 1, borderColor: 'grey.300', height: 24, mx: 1 }} />
                      <BlockButton format="bulleted-list" icon={FormatListBulleted} editor={editor} />
                      <BlockButton format="numbered-list" icon={FormatListNumbered} editor={editor} />
                    </Stack>
                  </Box>
                )}

                <Box
                  sx={{
                    p: 1,
                    '& [contenteditable]': {
                      outline: 'none',
                    },
                  }}
                >
                  <Editable
                    id="text-field"
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    placeholder={placeholder}
                    readOnly={disabled}
                    onKeyDown={onKeyDown}
                    style={{ minHeight: '180px' }}
                  />
                </Box>
              </Slate>
            </Box>

            {((errors[name] && errors[name]?.message) || helperText) && (
              <Typography variant="caption" sx={{ mt: 1 }} color={errors[name] ? 'error' : 'text.secondary'}>
                <ErrorComponent message={errors[name] && errors[name]?.message} />
              </Typography>
            )}
          </>
        )}
      />
    </Box>
  );
}
